import {Dialog, TextField} from "@mui/material";
import {defineMessage} from "react-intl";
import {
    FormDialogActionButtons,
    DialogActions,
    DialogContent,
    FormattedDialogTitle,
    FormContainer,
    FormattedFormRow,
    MultipleLineError,
} from "@dvrt/graphql-widgets";
import {AddUserLoginMutationVariables, useAddUserLoginMutation} from "./queries.generated";
import {commonTranslations} from "@/utils/translations";
import {useForm} from "@/utils/Form";
import {useDefaultLanguage} from "@/hooks";
import {FC} from "react";
import LanguageSelector from "@/components/LanguageSelector";
import {useAvailableTranslationsQuery} from "@/components/domain_settings/bulk_extension/bulkextensionqueries.generated";

type DataSource = {
    loading: boolean;
    // we do not need a refetch, because we only query language related stuff that won't change
    // after submitting this form, and the dialog will close anyway

    languages: string[];
    initialData: AddUserLoginMutationVariables;
};

const useDataSource = (domainId: number, userId: number, withAliases: boolean): DataSource => {
    const {data: languagesData, loading: languagesLoading} = useAvailableTranslationsQuery();
    const {defaultLanguage: language, loading: defaultLanguageLoading} = useDefaultLanguage({domainId});

    if (languagesLoading || defaultLanguageLoading)
        return {
            loading: true,
            languages: [],
            initialData: undefined,
        };

    return {
        loading: false,
        languages: languagesData?.availableTranslations,
        initialData: {
            loginData: {
                userId,
                withAliases,
                email: "",
                language,
            },
        },
    };
};

type DialogFormProps = {
    languages: string[];
    initialData: AddUserLoginMutationVariables;
    close: () => void;
    onSuccess: () => void;
};

const DialogForm: FC<DialogFormProps> = ({languages, initialData, close, onSuccess}) => {
    const form = useForm(useAddUserLoginMutation, "addUserLogin", initialData);

    const save = async () => {
        if (await form.submit()) {
            onSuccess();
        }
    };

    function closeAndReset() {
        form.reset();
        close();
    }

    return (
        <>
            <DialogContent>
                <FormContainer>
                    <FormattedFormRow label={commonTranslations.email}>
                        <TextField
                            value={form.values.loginData.email}
                            onChange={(event) => form.updateValues({loginData: {email: event.target.value}})}
                            variant="standard"
                            helperText={<MultipleLineError errors={form.errorHelper.fieldErrors("loginData.email")} />}
                            fullWidth
                            disabled={form.submitting}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={commonTranslations.language}>
                        <LanguageSelector
                            value={form.values.loginData.language}
                            variant="standard"
                            onChange={(language) => form.updateValues({loginData: {language}})}
                            languages={languages}
                            disabled={form.submitting}
                        />
                    </FormattedFormRow>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <FormDialogActionButtons
                    mainAction={save}
                    secondaryAction={() => form.reset()}
                    cancel={closeAndReset}
                    cancelDisabled={form.submitting}
                    enabled={form.hasChanged && !form.submitting}
                    pending={form.submitting}
                />
            </DialogActions>
        </>
    );
};

type Props = {
    domainId: number;
    userId: number;
    withAliases: boolean;
    isOpen: boolean;
    close: () => void;
    onSuccess: () => void;
};

const AddUserDialog: FC<Props> = ({domainId, userId, withAliases, isOpen, close, onSuccess}) => {
    const dataSource = useDataSource(domainId, userId, withAliases);

    if (dataSource.loading || !dataSource.initialData) return <Dialog open={isOpen} onClose={close} />;

    return (
        <Dialog open={isOpen} onClose={close}>
            <FormattedDialogTitle
                title={defineMessage({id: "userManagement.dialog.title", defaultMessage: "Add login"})}
            />
            <DialogForm
                languages={dataSource.languages}
                initialData={dataSource.initialData}
                close={close}
                onSuccess={onSuccess}
            />
        </Dialog>
    );
};

export default AddUserDialog;
